import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import { history, getLocale } from '@umijs/max';
import { getIntl } from 'umi';
import { message, App, ConfigProvider } from 'antd';
import React, { useEffect } from 'react';
import { AvatarDropdown, SelectLang } from '@/components';
import { NotificationIcon } from '@/components/RightContent/NotificationIcon';
import DeployNoticeBanner from '@/components/DeployNoticeBanner';
import CouponStockAlerts from '@/components/CouponStockAlerts';
import PaginationScrollToTop from '@/components/PaginationScrollToTop';
import ScrollToTopButton from '@/components/ScrollToTopButton';
import { QueryProvider } from '@/providers/QueryProvider';
import { useAuthStore } from '@/stores/authStore';
import { getAccessibleRoute } from '@/hooks/useAuth';
import createAccess from '@/access';
import { initSocket, disconnectSocket, subscribeToInquiries, subscribeToGeneralInquiries, subscribeToDepositRequests } from '@/utils/socket';
import type { API } from '@/services/ant-design-pro/typings';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { adminUserToCurrentUser } from '@/utils/authInitialState';
import { scrollToTopOnNewScreen } from '@/utils/routeScroll';
import { isSuperadminRole } from '@/utils/permissions';
import { initializeExchangeRates } from '@/hooks/useExchangeRate';
import '@ant-design/v5-patch-for-react-19';
import './global.css';

message.config({
  maxCount: 1,
});

/**
 * Page path → the `src/access.ts` boolean key that authorizes *viewing* that page.
 *
 * This is the single source of truth shared by the runtime route guard (`onPageChange`)
 * and the menu filter (`menuDataRender`). It MUST mirror `config/routes.ts` `access:`
 * (which Umi evaluates from `src/access.ts`), so a page that actually renders is never
 * bounced back by the guard and never hidden inconsistently in the menu.
 *
 * Why this matters: the previous maps checked a coarse `resource:read` permission
 * (e.g. `orders:read`) while the page renders on the broader access flag
 * (`canViewOrders`, which also accepts `order-statistics:view` / `order-status:*:view`).
 * A user granted only granular order permissions would see the page flash and then get
 * bounced to the landing page. The same divergence affected members (`users:write`-only),
 * etc. Delegating to the access object eliminates the whole class of bugs.
 *
 * Paths not listed here (detail pages, dynamic routes, groups, the /no-access notice)
 * are allowed by default.
 */
const ROUTE_ACCESS_MAP: Record<string, string> = {
  // Operations — per-page access (parent group uses canViewOperationSection)
  '/operation/operation-data': 'canViewOperationData',
  '/operation/category': 'canViewCategoryManagement',
  '/operation/stores': 'canViewCompanyMall',
  '/operation/promo-videos': 'canViewCompanyMall',
  '/operation/list': 'canViewLiveProducts',
  '/operation/add': 'canCreateProduct',
  '/operation/live-sellers': 'canViewLiveSellers',
  '/operation/live-team-list': 'canViewSellerTeams',
  // 옛 라이브 페이지 설정 경로는 리다이렉트 전용이라 맵에 넣지 않는다
  // (맵에 없으면 가드가 통과시키고, 실제 판정은 아래 새 경로에서 한 번만 일어난다).
  '/operation/frontend-pages': 'canViewFrontendPages',
  '/operation/rebate-settings/live-rebate-settings': 'canViewRebateSettings',
  '/operation/rebate-settings/share-rebate-settings': 'canViewShareRebateSettings',
  '/operation/coupon-point-settings': 'canViewCouponPointSettings',
  '/operation/search-management': 'canViewSearchManagement',
  '/operation/search-management/danger-management': 'canViewSearchManagement',
  '/operation/search-management/edit-search-term': 'canViewSearchManagement',
  '/operation/import-taobao': 'canImportTaobao',
  // Legacy rebate nested paths (if enabled later)
  '/operation/rebate-settings': 'canViewRebateSettings',
  '/operation/rebate-settings/rebate-level': 'canViewRebateSettings',
  '/operation/rebate-settings/rebate-rules': 'canViewRebateSettings',

  // Orders (list routes have no per-route access → governed by canViewOrders)
  '/orders/statistics': 'canViewOrderStatistics',
  '/orders/user': 'canViewOrders',
  '/orders/pending-payment': 'canViewOrders',
  '/orders/questions-detailed': 'canProcessOrderInquiry',

  // Storehouse / logistics — per-page access (parent group uses canViewStorehouse)
  '/storehouse/inspection-list': 'canViewInspectionList',
  '/storehouse/rack': 'canViewRacks',
  '/storehouse/warehouse-address': 'canViewWarehouseAddress',
  '/storehouse/waybill-informations': 'canViewWaybillInformations',
  '/storehouse/waybill-bandwidth-management': 'canViewWaybillBandwidth',
  '/storehouse/transportation-methods': 'canViewTransportationMethods',
  '/storehouse/regional-management': 'canViewRegionalManagement',
  '/storehouse/collect-orders': 'canViewCollectOrders',
  '/storehouse/goods': 'canViewGoodsManagement',
  '/logistics/waybill-informations': 'canViewWaybillInformations',
  '/logistics/waybill-bandwidth-management': 'canViewWaybillBandwidth',
  '/logistics/transportation-methods': 'canViewTransportationMethods',
  '/logistics/regional-management': 'canViewRegionalManagement',
  '/logistics/international-tracking-number-management': 'canViewInternationalTrackingNumber',
  '/logistics/hs-product-code-management': 'canViewHsProductCode',

  // Members
  '/members/statistics': 'canViewMemberStatistics',
  '/members/active-users': 'canViewActiveUsers',
  '/members/general': 'canViewMembers',
  '/members/user-promotion-detail': 'canViewMembers',
  '/members/coupons-detailed': 'canViewMembers',

  // Statistics
  '/statistics/visit-analytics': 'canViewSiteAnalytics',

  // Settlement
  '/settlement/deposit-application-history': 'canViewDepositApplicationHistory',
  '/settlement/deposit-usage-history': 'canViewDepositUsageHistory',
  '/settlement/financial-record': 'canViewFinancialRecords',
  '/settlement/deposit': 'canViewDeposit',
  '/settlement/pending-payment': 'canViewPendingPayment',
  '/settlement/share-rebates': 'canViewShareRebates',
  '/settlement/deposit-application-detailed': 'canViewSettlement',
  '/settlement/deposit-usage-detailed': 'canViewSettlement',
  '/settlement/points-content': 'canViewSettlement',
  '/settlement/consumption-records-content': 'canViewSettlement',
  '/settlement/coupon-usage-history-content': 'canViewSettlement',
  '/settlement/rebate-records-content': 'canViewSettlement',
  '/settlement/tax-settlement-content': 'canViewSettlement',

  // Customer Service
  '/cs/inquiries': 'canProcessGeneralInquiry',
  '/cs/notices': 'canManageCS',
  '/cs/suggestion': 'canManageCS',
  '/cs/reviews': 'canManageCS',
  '/cs/help': 'canViewHelpCenter',
  '/cs/banners-detailed': 'canViewBanners',

  // Settings — per-page gates (parent group uses canViewSettingsSection)
  '/settings/admins': 'canViewAdmins',
  '/settings/rates': 'canViewExchangeTariffSetting',
  '/settings/additional-service': 'canViewAddOnServices',
  '/settings/purchase-condition-settings': 'canViewPurchaseConditionSetting',
  '/settings/work-log': 'canViewWorkerLog',
  '/settings/island-mountain-cost': 'canViewIslandMountainCost',
  '/settings/product-price-increase': 'canViewProductPriceIncrease',
};

/**
 * Root container wrapper - wraps entire app with QueryProvider
/**
 * Socket Initializer Component - Initializes socket connection when app starts
 */
const SocketInitializer: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const authState = useAuthStore((state) => state);
  const isAuthenticated = authState.isAuthenticated;
  const userPermissions = authState.user?.permissions || [];
  const userRole = authState.user?.role;
  const isFullAdmin = isSuperadminRole(userRole);

  const canViewMemberRechargeReview =
    isFullAdmin || userPermissions.includes('deposit-application-history:read');
  const canViewMemberWithdrawalReview =
    isFullAdmin || userPermissions.includes('deposit-usage-history:read');

  useEffect(() => {
    if (!isAuthenticated) {
      // Tear the socket down on logout so the next login builds a fresh
      // connection with the new admin's token (rooms are permission-based).
      disconnectSocket();
      return;
    }

    let cancelled = false;
    let activeSocket: ReturnType<typeof initSocket> | null = null;
    let onConnectSubscribe: (() => void) | null = null;

    /** Defer socket so first paint / REST calls are not competing with the handshake on high-latency links. */
    const deferMs = 350;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (!useAuthStore.getState().isAuthenticated) return;

      const socket = initSocket();
      activeSocket = socket;
      if (!socket) return;

      const subscribeAll = () => {
        subscribeToInquiries(() => {});
        subscribeToGeneralInquiries(() => {});
        if (canViewMemberRechargeReview || canViewMemberWithdrawalReview) {
          subscribeToDepositRequests(() => {}, () => {});
        }
      };
      onConnectSubscribe = subscribeAll;

      // Always keep the 'connect' handler bound (not only while disconnected):
      // it must re-run after every reconnect, or room subscriptions silently
      // stop coming back once the server restarts.
      socket.on('connect', subscribeAll);
      if (socket.connected) {
        subscribeAll();
      }
    }, deferMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (activeSocket && onConnectSubscribe) {
        activeSocket.off('connect', onConnectSubscribe);
      }
    };
  }, [isAuthenticated, canViewMemberRechargeReview, canViewMemberWithdrawalReview]);

  return children;
};

/**
 * Root container wrapper - wraps entire app with QueryProvider.
 * Ensures QueryProvider is available even for pages with layout: false.
 */
export function rootContainer(container: React.ReactElement): React.ReactElement {
  return (
    <ConfigProvider>
      <QueryProvider>
        <App>
          <PaginationScrollToTop />
          <SocketInitializer>
            {container}
          </SocketInitializer>
        </App>
      </QueryProvider>
    </ConfigProvider>
  );
}

const isDev = process.env.NODE_ENV === 'development';
const isDevOrTest = isDev || process.env.CI;
const loginPath = '/user/login';

// Server URL from environment or default
const SERVER_URL = process.env.SERVER_URL;

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // Check if we're on login page - don't try to get user
  const { location } = history;
  const isLoginPage = location.pathname === loginPath;

  // Warm exchange-rate cache only for authenticated sessions (admin API requires auth).
  if (!isLoginPage && useAuthStore.getState().isAuthenticated) {
    initializeExchangeRates().catch(() => {});
  }

  // Default to Chinese when the user has not chosen a language yet (login page + first login).
  try {
    const savedLocale = localStorage.getItem('umi_locale');
    if (!savedLocale) {
      localStorage.setItem('umi_locale', 'zh-CN');
    }
  } catch (e) {
    // console.error('Error setting default locale:', e);
  }

  // Get user from Zustand store (persisted) - only if not on login page
  let currentUser: API.CurrentUser | undefined ;
  
  if (!isLoginPage) {
    try {
      const { user, isAuthenticated } = useAuthStore.getState();
      
      // Use stored user and permissions directly (no API call)
      if (user && isAuthenticated) {
        currentUser = adminUserToCurrentUser(user);
      }
    } catch (error) {
      // console.error('❌ Error getting auth state:', error);
    }
  }

  const fetchUserInfo = async () => {
    try {
      const { user, isAuthenticated } = useAuthStore.getState();
      
      // Use stored user and permissions directly (no API call)
      if (user && isAuthenticated) {
        return adminUserToCurrentUser(user);
      }
    } catch (error) {
      // console.error('Error fetching user info:', error);
    }
    return undefined;
  };

  const result = {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
  
  return result;
}

// Component to set document title dynamically
const DocumentTitle: React.FC<{ title: string }> = ({ title }) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout = ({
  initialState,
  setInitialState,
}: {
  initialState: {
    settings?: Partial<LayoutSettings>;
    currentUser?: API.CurrentUser;
    loading?: boolean;
    fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  };
  setInitialState: (initialState: any) => void;
}) => {
  // Get current locale and translated title from Umi's already-loaded i18n,
  // instead of pulling all locale bundles into the entry chunk just for one string.
  const locale = getLocale();
  const translatedTitle =
    getIntl(locale).formatMessage({ id: 'pages.layouts.adminPanel.title' }) || 'Admin Panel';

  /** Space between language block and account avatar */
  const headerLangToAvatarGap = 16;
  /** Notifications and language are separate action slots; extra space from notification cluster to language */
  const headerNotifyToLangGap = 22;

  return {
    actionsRender: () => [
      <div
        key="header-notifications"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
          marginLeft: 'auto',
        }}
      >
        <NotificationIcon />
      </div>,
      <div
        key="header-lang"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
          marginLeft: headerNotifyToLangGap,
        }}
      >
        <SelectLang />
      </div>,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      style: { marginLeft: headerLangToAvatarGap },
      render: () => <AvatarDropdown />,
    },
    onPageChange: () => {
      const { location } = history;
      scrollToTopOnNewScreen(location.pathname, history.action);
      
      // Handle /admin (without trailing slash) - redirect to /admin/home
      // base is /admin/, so /admin becomes / in route matching
      // But if pathname is exactly /admin, we need to redirect
      if (location.pathname === '/admin' || location.pathname === '/admin/') {
        const { user: u } = useAuthStore.getState();
        history.replace(getAccessibleRoute(u));
        return;
      }
      
      // Don't redirect if already on login page
      if (location.pathname === loginPath) {
        return;
      }
      
      const { isAuthenticated, user } = useAuthStore.getState();
      // 如果没有登录，重定向到 login
      if (!isAuthenticated && !initialState?.currentUser) {
        history.push(loginPath);
        return;
      }

      // Check route access permissions based on pathname
      if (isAuthenticated && user) {
        const pathname = location.pathname;

        // Only superadmin bypasses route permission checks
        if (isSuperadminRole(user.role)) {
          return;
        }

        // Authorize the route with the SAME access object that config/routes.ts evaluates
        // (src/access.ts). Keeping the guard in lockstep with what actually renders prevents
        // the page from flashing and then bouncing back. Paths not in ROUTE_ACCESS_MAP
        // (detail/dynamic pages, the /no-access notice, etc.) are allowed.
        const requiredAccessKey = ROUTE_ACCESS_MAP[pathname];

        if (requiredAccessKey) {
          const access = createAccess({
            currentUser: initialState?.currentUser,
          }) as Record<string, unknown>;

          if (!access[requiredAccessKey]) {
            message.warning(
              'You do not have permission to access this page. Redirecting to dashboard...',
            );

            // Redirect to an accessible page after a short delay
            setTimeout(() => {
              history.replace(getAccessibleRoute(user));
            }, 1000);
          }
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    // Filter menu items based on todaymall permissions
    menuDataRender: (menuData: any[]) => {
      const authState = useAuthStore.getState();
      const userRole = initialState?.currentUser?.role || authState.user?.role;

      // Only superadmin sees all menus without permission filtering
      if (isSuperadminRole(userRole)) {
        return menuData;
      }

      // Filter using the SAME access object as the route guard and config/routes.ts
      // (src/access.ts), so the menu and the guard can never disagree. Items whose path is
      // not in ROUTE_ACCESS_MAP (groups, detail pages) stay visible; empty parent groups
      // are pruned.
      const access = createAccess({
        currentUser: initialState?.currentUser,
      }) as Record<string, unknown>;

      const resolveMenuAccessKey = (item: { path?: string; access?: string }): string | undefined => {
        if (item.path && ROUTE_ACCESS_MAP[item.path]) {
          return ROUTE_ACCESS_MAP[item.path];
        }
        if (typeof item.access === 'string') {
          return item.access;
        }
        return undefined;
      };

      const filterMenu = (items: any[]): any[] => {
        return items.filter((item) => {
          const requiredAccessKey = resolveMenuAccessKey(item);
          if (requiredAccessKey && !access[requiredAccessKey]) {
            return false;
          }

          const childItems = item.children ?? item.routes;
          if (Array.isArray(childItems) && childItems.length > 0) {
            const filteredChildren = filterMenu(childItems);
            if (item.children) {
              item.children = filteredChildren;
            } else {
              item.routes = filteredChildren;
            }
            return filteredChildren.length > 0;
          }

          return true;
        });
      };

      return filterMenu(menuData);
    },
    // menuHeaderRender: () => (
    //   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0' }}>
    //     <img src="/admin/logo.png" alt="logo" style={{ height: 32, objectFit: 'contain' }} />
    //   </div>
    // ),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children: any) => {
      // if (initialState?.loading) return <PageLoading />;
      // QueryProvider is now in rootContainer, so we don't need it here
      return (
        <>
          {/* 테스트 빌드는 탭 제목 앞에 [TEST] (config.ts 의 TEST_TITLE_PREFIX) */}
          <DocumentTitle title={`${process.env.TEST_TITLE_PREFIX || ''}${translatedTitle}`} />
          <DeployNoticeBanner />
          <CouponStockAlerts />
          {children}
          <ScrollToTopButton />
          {isDevOrTest && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState: any) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
    title: translatedTitle,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: any = {
  // In dev/test we rely on Umi proxy (see `config/proxy.ts`) so we must keep requests
  // on the same origin. Otherwise browsers enforce CORS and some admin endpoints fail.
  baseURL: isDevOrTest ? '' : SERVER_URL,
  ...errorConfig,
  // Merge request interceptors
  requestInterceptors: [
    ...(errorConfig.requestInterceptors || []),
    (config: any) => {
      // Ensure baseURL is set
      if (!config.baseURL) {
        config.baseURL = isDevOrTest ? '' : SERVER_URL;
      }
      const fullUrl = `${config.baseURL || SERVER_URL}${config.url || ''}`;
      config.headers = {
        ...config.headers,
        'X-Locale': getLocale().split('-')[0],  // Send only language code (ko, en, zh)
      };
      // console.log('📤 API Request:', {
      //   method: config.method,
      //   url: config.url,
      //   baseURL: config.baseURL || SERVER_URL,
      //   fullUrl: fullUrl,
      //   data: config.data ? { ...config.data, password: config.data.password ? '***' : undefined } : undefined,
      //   headers: config.headers,
      // });
      return config;
    },
  ],
  // Merge response interceptors
  responseInterceptors: [
    ...(errorConfig.responseInterceptors || []),
    (response: any) => {
      const fullUrl = `${response.config?.baseURL || SERVER_URL}${response.config?.url || ''}`;
      // console.log('📥 API Response:', {
      //   status: response.status,
      //   statusText: response.statusText,
      //   data: response.data,
      //   url: response.config?.url,
      //   baseURL: response.config?.baseURL || SERVER_URL,
      //   fullUrl: fullUrl,
      // });
      return response;
    },
  ],
};
